<template>
  <div class="bg-main h-full pt-12">
    <section
      v-if="product && (!isSeller || product.brandName === 'Ullmax')"
      itemscope
      itemtype="https://schema.org/Product"
    >
      <BreadCrumbs v-if="!uiStore.isMobile" :product="product" />
      <div class="container xxl:px-24 flex flex-wrap relative">
        <ProductPageMeta
          :product="product"
          :route="route"
          :currency-code="currencyCode"
        />

        <div
          ref="productImages"
          class="
            productImages
            -mx-24
            sm:-mx-56
            md:mx-0
            order-1
            md:order-2
            w-screen
            md:w-2/3 md:pr-56
            relative
          "
        >
          <ProductImages
            :product="product"
          />
        </div>
        <div
          ref="productInformation"
          class="
            productInformation
            order-3
            w-full
            pt-16
            relative
            md:pt-0 md:w-1/3
            z-above
          "
        >
          <BreadCrumbs
            v-if="uiStore.isMobile"
            class="breadcrumbs-mobile"
            :product="product"
          />

          <div class="w-full md:sticky top-88 product-info">
            <BuyProduct
              :product="product"
              @toggle-product-review="toggleProductReview"
              @toggle-stock-notification="toggleStockNotification"
              @toggle-product-information="toggleProductInformation"
              @toggle-size-guide="toggleSizeGuide"
              @on-item-change="onItemChange"
            />

            <div class="mt-40 w-full text-primary font-medium">
              <div
                class="
                  py-24
                  pr-12
                  border-b border-border
                  flex
                  items-center
                  cursor-pointer
                "
                @click="toggleProductDeliveryInformation"
              >
                <span>
                  <fa class="mr-6" :icon="['far', 'truck']" />
                  {{ $lang('product_page', 'delivery_time_action') }}
                </span>
                <fa class="ml-auto" :icon="['fal', 'chevron-right']" />
              </div>
              <div
                class="
                  py-24
                  pr-12
                  border-b border-border
                  flex
                  items-center
                  cursor-pointer
                "
                @click="toggleProductInformation"
              >
                <span>
                  <fa class="mr-6" :icon="['far', 'tshirt']" />
                  {{ $lang('product_page', 'product_information_action') }}
                </span>
                <fa class="ml-auto" :icon="['fal', 'chevron-right']" />
              </div>
              <div class="py-24 pr-12 flex border-b border-border items-center cursor-pointer" @click="toggleProductReview">
                <span>
                  <fa class="mr-6" :icon="['far', 'star']" />
                  {{ $lang('product_page', 'review_action') }}
                  <span v-if="reviewLength">({{ reviewLength }})</span>
                </span>
                <fa class="ml-auto" :icon="['fal', 'chevron-right']" />
              </div>
              <div class="py-24 pr-12 flex items-center cursor-pointer" @click="toggleProductReview">
                <span>
                  <fa class="mr-6" :icon="['fal', 'question-circle']" />
                  {{ $lang('product_page', 'faq') }}
                  <span v-if="questionLength">({{ questionLength }})</span>
                </span>
                <fa class="ml-auto" :icon="['fal', 'chevron-right']" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div v-if="product">
      <ComponentWrapper
        v-if="!_isNotEmpty(pageDocument.components)"
        :components="pageDocument.components"
      />

      <ProductHistorySwiper
        :history="productHistory"
        :has-match-with-products="product.hasMatchWithProducts"
        :match-with-products="product.matchWithProducts"
      />

      <div v-if="pageDocument.components.length > 0">
        <component
          :is="components[component.layout]"
          v-for="(component, idx) in pageDocument.components"
          :key="component.layout + idx"
          v-bind="{ component }"
        />
      </div>

      <ProductStockNotification
        v-if="stockNotificationToggled"
        :product="product"
        :item="selectedItem.item"
        @on-stock-notification-close="toggleStockNotification"
      />

      <ProductInformation
        v-if="productInformationToggled"
        :product="product"
        @on-product-information-close="toggleProductInformation"
      />

      <ProductSizeGuide
        v-if="sizeGuideToggled"
        :product="product"
        @on-size-guide-close="toggleSizeGuide"
      />

      <ProductReview
        v-if="productReviewToggled"
        :product="product"
        @on-product-review-close="toggleProductReview"
      />

      <ProductDeliveryInformation
        v-if="productDeliveryInformationToggled"
        :product="product"
        @on-product-delivery-information-close="toggleProductDeliveryInformation"
      />

      <client-only>
        <div
          v-if="!yotpoLoaded"
          class="yotpo yotpo-main-widget h-0 w-0 overflow-hidden"
          :data-product-id="product.sku"
          :data-price="product.priceAsNumber"
          :data-currency="currencyCode"
          :data-name="product.title"
          :data-url="itemPath"
          :data-image-url="product.standardImage"
        />
      </client-only>
    </div>
  </div>
</template>

<script setup lang="ts">
import ProductPage from '~/models/product-page';
import { useUiStore } from '~/store/ui';
import { useCartStore } from '~/store/cart';
import Product, { type ProductType } from '~/models/product';
import { useConfigStore } from '~/store/config';
import BlockComponents from '~/constants/block-components';
import type { CountryCodes } from '~/constants/types/cart';
import type { WpPageApiResponse } from '~/constants/types/wp-page';
import BreadCrumbs from '~/components/productpage/BreadCrumbs.vue';
import type { YotpoProductReview } from '~/constants/types/yotpo-product-review';
import ProductImages from '~/components/productpage/ProductImages.vue';
import pkg from 'lodash';
const { isEmpty } = pkg;
import BuyProduct from '~/components/productpage/BuyProduct.vue';
import ProductInformation from '~/components/productpage/ProductInformation.vue';
import ProductSizeGuide from '~/components/productpage/ProductSizeGuide.vue';
import ProductReview from '~/components/productpage/ProductReview.vue';
import ProductDeliveryInformation from '~/components/productpage/ProductDeliveryInformation.vue';
import ProductStockNotification from '~/components/productpage/ProductStockNotification.vue';
import ProductHistorySwiper from '~/components/productpage/ProductHistorySwiper.vue';
import ComponentWrapper from '~/components/global/ComponentWrapper.vue';
import ProductPageMeta from '~/components/productpage/ProductPageMeta.vue';
import dayjs from 'dayjs';
import { pushToDataLayer } from '~/composables/useTracking';
import type { ProductHistory } from '~/constants/types/product-history';

const config = useConfigStore();
const { $lang } = useNuxtApp();
const { fetchProduct } = useProductService();
const uiStore = useUiStore();
const cartStore = useCartStore();
const runtimeConfig = useRuntimeConfig();
const { apiGet } = useApiFetch();

const isSeller = useCookie<boolean>('isSeller', { default: () => false, maxAge: 3600 * 24 * 182, path: '/' });

const props = defineProps<{
  page: WpPageApiResponse,
  hasSiteWideMessage?: boolean,
}>();

const stockNotificationToggled = ref(false);
const productInformationToggled = ref(false);
const sizeGuideToggled = ref(false);
const productReviewToggled = ref(false);
const productDeliveryInformationToggled = ref(false);
const yotpoLoaded = ref(false);
const reviewLength = ref(0);
const questionLength = ref(0);

const yotpoProductReview = ref<YotpoProductReview>({
  bottomline: {
    average_score: 0,
    custom_fields_bottomline: 0,
    organic_average_scrore: 0,
    star_distribution: {},
    total_organic_reviews: 0,
    total_review: 0,
  },
  reviews: [],
  questions: [],
});

const pageDocument = computed(()=> {
  return new ProductPage({ contentData: props.page });
});

const fetchedProduct = ref<ProductType | null>(null);
const selectedItem = ref<any>(null);

const { data } = await useAsyncData('productContentLoad', async() => {
  return await fetchProduct(pageDocument.value.ecomId);
});

if (data.value) {
  fetchedProduct.value = data.value;
  if (data.value.ecomData.items.length === 1) {
    selectedItem.value = data.value.ecomData.items[0];
  }
} else {
  throw createError({
    statusCode: 404,
    statusMessage: 'Not found',
  });
}

const product = computed<Product | null>(()=> {
  if (fetchedProduct.value) {
    if (!fetchedProduct.value.ecomData.bundleInfo) {
      fetchedProduct.value.ecomData.bundleInfo = null;
    }
    return new Product({
      ecomData: fetchedProduct.value.ecomData,
      contentData: fetchedProduct.value.contentData,
    });
  } else {
    return null;
  }
});

const components = computed<typeof BlockComponents>(()=> {
  return BlockComponents;
});

const currencyCode = computed<string>(()=> {
  const fallBackMarkets = {
    SE: 'SEK',
    FI: 'EUR',
    NO: 'NOK',
  };
  const fallBack = fallBackMarkets[config.market.country as CountryCodes];

  return cartStore.cartData.currency || fallBack;
});

const itemPath = computed<string>(()=> {
  return `${runtimeConfig.public.frontendUrl}${route}`;
  //return `${process.env.FRONTEND_URL}${this.$route.fullPath}`;
});

const productHistory = computed<ProductHistory>(()=> {
  return {
    currencyCode: currencyCode.value,
    title: pageDocument.value.title,
    ecomId: pageDocument.value.ecomId,
  };
});

const route = computed<string>(()=> {
  return '';
  //return process.env.FRONTEND_URL + this.$router.currentRoute.path;
});

const toggleProductInformation = () => {
  productInformationToggled.value = !productInformationToggled.value;
};
const toggleSizeGuide = () => {
  sizeGuideToggled.value = !sizeGuideToggled.value;
};
const toggleProductReview = () => {
  productReviewToggled.value = !productReviewToggled.value;
  if (productReviewToggled.value) {
    setTimeout(() => {
      refreshYotpo();
    }, 250);
  }
};

const toggleProductDeliveryInformation = () => {
  productDeliveryInformationToggled.value = !productDeliveryInformationToggled.value;
};
const toggleStockNotification = () => {
  stockNotificationToggled.value = !stockNotificationToggled.value;
};

const refreshYotpo = async() => {
  yotpoLoaded.value = false;

  // this timeout is to be sure that window yotpo script is loaded
  const interval = setInterval(async() => {
    if (yotpoLoaded.value) {
      clearInterval(interval);
      return;
    }

    if (!window.Yotpo) {
      return;
    }
    if (window.yotpo?.userSettings) {
      window.yotpo.userSettings.language = config.market.language;
    }
    const api = new window.Yotpo.API(window.yotpo);
    api?.refreshWidgets();

    yotpoLoaded.value = true;
    const appKey = window.yotpo?.appKey;

    if (product.value) {
      try {
        const data = await apiGet(`https://api-cdn.yotpo.com/v1/widget/${appKey}/products/${product.value.sku}/reviews.json?per_page=1000`);

        const { bottomline, reviews, questions } =
        (data?.response as YotpoProductReview) || yotpoProductReview.value;
        yotpoProductReview.value = {
          bottomline,
          reviews,
          questions,
        };
        reviewLength.value = data?.response?.reviews?.length || 0;

      } catch (e) {
        console.log(e);
      }
    }
  }, 200);
};

const _isNotEmpty = (value: any) => {
  return typeof value !== 'undefined' && !isEmpty(value);
};

const onItemChange = (item: any) => {
  selectedItem.value = item;
};

onMounted(()=> {
  refreshYotpo();
  sendGtmProductDetail();
});
const sendGtmProductDetail = () => {
  if (product.value) {
    const payload = {
      event: 'productDetail',
      ecommerce: {
        currencyCode: currencyCode.value,
        detail: {
          products: [
            {
              id: product.value.sku,
              feedId: product.value.sku,
              ...product.value.gtmProductValues,
            },
          ],
        },
      },
    };
    pushToDataLayer(payload);
  }
};

</script>

<style>
.productInformation {
  .breadcrumbs {
    @apply pt-0;
  }

  .excerpt>>>p {
    @apply inline;
  }

  >>>.yotpo-stars {
    @apply mr-8;
  }
}
</style>
